import { EXPENSE_STATUS_CODE, MEETING_EXPENSE_CATEGORY, MEETING_EXPENSE_FIELDS } from '../../classes/meeting-expense/meeting-expense.constants';

const EXPENSE_ATTRIBUTES = `
    <attribute name="indskr_expenseid" />
    <attribute name="indskr_name" />
    <attribute name="indskr_date" />
    <attribute name="indskr_amount" />
    <attribute name="statecode" />
    <attribute name="statuscode" />
    <attribute name="${MEETING_EXPENSE_FIELDS.TYPE}" />
    <attribute name="${MEETING_EXPENSE_FIELDS.SUBTYPE}" />
    <attribute name="indskr_customerid" />
    <attribute name="ownerid" />
    <attribute name="${MEETING_EXPENSE_FIELDS.REGARDING}" />`;

const IS_MEETING = `<condition attribute="indskr_expensecategories" operator="eq" value="${MEETING_EXPENSE_CATEGORY}" />`;

const EXCLUDE_CANCELLED = `<condition attribute="statuscode" operator="ne" value="${EXPENSE_STATUS_CODE.CANCELLED}" />`;

export const MEETING_EXPENSE_FETCH_XML = {
  byMeeting: `<fetch version="1.0" mapping="logical">
  <entity name="indskr_expense">${EXPENSE_ATTRIBUTES}
    <filter type="and">
      <condition attribute="${MEETING_EXPENSE_FIELDS.REGARDING}" operator="eq" value="{meetingId}" />
      ${EXCLUDE_CANCELLED}
    </filter>
  </entity>
</fetch>`,
  byIds: `<fetch version="1.0" mapping="logical">
  <entity name="indskr_expense">${EXPENSE_ATTRIBUTES}
    <filter type="and">
      <condition attribute="indskr_expenseid" operator="in">{idValues}</condition>
    </filter>
  </entity>
</fetch>`,
  notesByObjectIds: `<fetch version="1.0" mapping="logical">
  <entity name="annotation">
    <attribute name="annotationid" />
    <attribute name="notetext" />
    <attribute name="filename" />
    <attribute name="filesize" />
    <attribute name="mimetype" />
    <attribute name="isdocument" />
    <attribute name="createdon" />
    <attribute name="objectid" />
    <attribute name="ownerid" />
    <order attribute="createdon" descending="false" />
    <filter type="and">
      <condition attribute="objectid" operator="in">{idValues}</condition>
    </filter>
  </entity>
</fetch>`,
  meetingTypes: `<fetch version="1.0" mapping="logical">
  <entity name="indskr_expense_type">
    <attribute name="indskr_expense_typeid" />
    <attribute name="indskr_name" />
    <order attribute="indskr_name" descending="false" />
    <filter type="and">
      <condition attribute="statecode" operator="eq" value="0" />
      ${IS_MEETING}
    </filter>
  </entity>
</fetch>`,
  meetingSubtypes: `<fetch version="1.0" mapping="logical">
  <entity name="indskr_expense_category">
    <attribute name="indskr_expense_categoryid" />
    <attribute name="indskr_name" />
    <order attribute="indskr_name" descending="false" />
    <filter type="and">
      <condition attribute="statecode" operator="eq" value="0" />
      ${IS_MEETING}
    </filter>
  </entity>
</fetch>`,
  typeSubtypePairs: `<fetch version="1.0" mapping="logical">
  <entity name="indskr_expense_type">
    <attribute name="indskr_expense_typeid" />
    <filter type="and">
      <condition attribute="statecode" operator="eq" value="0" />
      ${IS_MEETING}
    </filter>
    <link-entity name="indskr_indskr_expense_type_indskr_expense_cate" from="indskr_expense_typeid" to="indskr_expense_typeid" intersect="true" link-type="inner">
      <attribute name="indskr_expense_categoryid" alias="subtypeId" />
    </link-entity>
  </entity>
</fetch>`,
};

export const toFetchInValues = (ids: string[]): string => ids.map((id) => `<value>${id}</value>`).join('');

// Keeps each `in` list short enough for a GET URL.
export const FETCH_IN_CHUNK = 50;

export function chunkIds(ids: string[], size = FETCH_IN_CHUNK): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

/** The meeting dates the device keeps; expenses follow their meeting on and off the device. */
export interface MeetingSyncWindow {
  fromIso: string;
  toIso: string;
}

/**
 * The rep's own meeting expenses plus those on meetings where the rep is a co-visitor, on meetings inside the sync window.
 * A delta returns every status, so a record cancelled elsewhere reaches the device and is removed there.
 */
export function meetingExpenseSyncFetchXml(meetingWindow: MeetingSyncWindow, modifiedSinceIso: string | null): string {
  const delta = modifiedSinceIso
    ? `<condition attribute="modifiedon" operator="ge" value="${modifiedSinceIso}" />`
    : EXCLUDE_CANCELLED;
  return syncScopeFetchXml(EXPENSE_ATTRIBUTES, meetingWindow, delta);
}

/**
 * Ids of every live expense in the sync scope. A delta only sees rows whose own `modifiedon` moved, so this finds
 * rows that left the scope (reassigned, unshared, deleted) and rows that entered it (co-visitor added, meeting rescheduled).
 */
export function meetingExpenseSyncIdsFetchXml(meetingWindow: MeetingSyncWindow): string {
  return syncScopeFetchXml(`<attribute name="${MEETING_EXPENSE_FIELDS.ID}" />`, meetingWindow, EXCLUDE_CANCELLED);
}

// Paging a distinct query with link-entities is only stable with an order; a skipped id would be pruned.
function syncScopeFetchXml(attributes: string, meetingWindow: MeetingSyncWindow, statusCondition: string): string {
  return `<fetch version="1.0" mapping="logical" distinct="true">
  <entity name="indskr_expense">${attributes}
    <order attribute="${MEETING_EXPENSE_FIELDS.ID}" />
    <filter type="and">
      <condition attribute="${MEETING_EXPENSE_FIELDS.CATEGORY}" operator="eq" value="${MEETING_EXPENSE_CATEGORY}" />
      <condition attribute="${MEETING_EXPENSE_FIELDS.REGARDING}" operator="not-null" />
      <condition entityname="appt" attribute="scheduledstart" operator="ge" value="${meetingWindow.fromIso}" />
      <condition entityname="appt" attribute="scheduledstart" operator="le" value="${meetingWindow.toIso}" />
      ${statusCondition}
      <filter type="or">
        <condition attribute="ownerid" operator="eq-userid" />
        <condition entityname="cov" attribute="indskr_user" operator="eq-userid" />
      </filter>
    </filter>
    <link-entity name="appointment" from="activityid" to="${MEETING_EXPENSE_FIELDS.REGARDING}" link-type="outer" alias="appt">
      <link-entity name="indskr_accompanieduser" from="indskr_appointment" to="activityid" link-type="outer" alias="cov" />
    </link-entity>
  </entity>
</fetch>`;
}
